import React, { useState, useEffect } from "react";
import { loadSection } from "../../services/contentSource";
import teamFallback from "../../data/home/team.json";

const NETWORKING_API_URL = "https://networking.cognisun.net:4015";

interface MemberApiData {
  profilepicture?: string;
  fullName?: string;
  occupation?: string;
}

interface TeamMember {
  name: string;
  role: string;
  image: string;
  link: string;
  fadeFrom: string;
  delay: string;
  id: string;
}

interface TeamSectionProps {
  badgeText?: string;
}

const TeamSection: React.FC<TeamSectionProps> = ({
  badgeText = "07. LEADERSHIP & ARCHITECTS",
}) => {
  const [teamData, setTeamData] = useState<any>(teamFallback);
  const [memberData, setMemberData] = useState<Record<string, MemberApiData>>({});

  useEffect(() => {
    loadSection("team", teamFallback as any)
      .then(({ data: main }) => {
        if (main) setTeamData(main);
      })
      .catch((err) => {
        console.warn("Using fallback team data:", err);
      });
  }, []);

  useEffect(() => {
    if (!teamData) return;
    const membersWithIds = (teamData.members || []).filter((m: TeamMember) => m.id);
    membersWithIds.forEach(async (member: TeamMember) => {
      try {
        const response = await fetch(`${NETWORKING_API_URL}/member/getMemberDetails`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ personid: member.id }),
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data?.[0]) {
            const data = result.data[0];
            setMemberData((prev) => ({
              ...prev,
              [member.id]: {
                profilepicture: data.profilepicture ? `${NETWORKING_API_URL}/${data.profilepicture}` : undefined,
                fullName: data.fullName,
                occupation: data.occupation,
              },
            }));
          }
        }
      } catch (error) {
        console.error(`Failed to fetch data for ${member.name}:`, error);
      }
    });
  }, [teamData]);

  const handleMemberClick = (member: TeamMember) => {
    if (member.id) window.open(`/card/${member.id}`, "_blank");
  };

  const getImageSrc = (member: TeamMember) => {
    if (member.id && memberData[member.id]?.profilepicture) return memberData[member.id].profilepicture as string;
    return member.image;
  };

  const getCleanRole = (member: TeamMember) => {
    const rawRole = (member.id && memberData[member.id]?.occupation) || member.role || "";
    // Clean up any double "MindSyncX, MindSyncX" or typo
    let cleaned = rawRole
      .replace(/,?\s*MindSyncX/gi, "")
      .replace(/Architech/gi, "Architect")
      .trim();

    if (!cleaned) cleaned = "AI Executive";
    return cleaned;
  };

  if (!teamData) return null;

  return (
    <section className="team-area mx-team-revamp" id="team">
      <div className="container mx-container">
        <div className="mx-team-header">
          <div className="mx-hero-badge">
            <span className="mx-hero-badge-icon">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </span>
            <span>{badgeText}</span>
          </div>

          <h2 className="mx-team-title">
            THE LEADERSHIP TEAM BEHIND MINDSYNCX
          </h2>

          <p className="mx-team-lede">
            {teamData.subtitle ||
              "A team of visionaries, engineers, and creators — shaping the future of AI, one breakthrough at a time."}
          </p>
        </div>

        <div className="mx-team-grid">
          {(teamData.members || []).map((member: TeamMember, index: number) => {
            const role = getCleanRole(member);

            return (
              <div
                className="mx-team-card"
                key={index}
                onClick={() => handleMemberClick(member)}
                style={{ cursor: member.id ? "pointer" : "default" }}
              >
                <div className="mx-team-thumb-wrap">
                  <img
                    src={getImageSrc(member)}
                    alt={member.name}
                    className="mx-team-thumb"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="mx-team-thumb-gradient"></div>

                  <div className="mx-team-role-pill">
                    <span className="mx-about-dot-pulse"></span>
                    <span>{role.toUpperCase()}</span>
                  </div>
                </div>

                <div className="mx-team-info">
                  <div className="mx-team-name-row">
                    <h3 className="mx-team-name">{member.name}</h3>
                    {member.id && (
                      <span className="mx-team-card-icon" title="View Digital Card">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="7" y1="17" x2="17" y2="7"></line>
                          <polyline points="7 7 17 7 17 17"></polyline>
                        </svg>
                      </span>
                    )}
                  </div>
                  <p className="mx-team-corp">MindSyncX Enterprise Leadership</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
